import dayjs, { type Dayjs } from 'dayjs';
import { useMemo } from 'react';
import type { GetDriverWindowSlotsQuery } from '@/generated/graphql/graphql';
import {
  useGetDriverDistricts,
  useGetDriverWindowSlots,
} from '@/views/districts-new/query/calendarGrid.hooks';

export interface TrackingPolygon {
  id: string;
  name: string | null | undefined;
  coordinates: { lat: number; lng: number }[];
  color: string;
}

const POLYGON_COLORS = [
  '#4285F4',
  '#EA4335',
  '#34A853',
  '#FF6D01',
  '#46BDC6',
  '#7B1FA2',
  '#C2185B',
  '#0097A7',
  '#689F38',
  '#F57F17',
  '#5D4037',
  '#455A64',
  '#E91E63',
  '#3F51B5',
  '#009688',
  '#795548',
  '#607D8B',
  '#FF5722',
  '#CDDC39',
  '#00BCD4',
];

function getDbWeekday(date: Dayjs): number {
  const day = date.day();
  return day === 0 ? 7 : day;
}

function isTempDistrictActive(
  date: Dayjs,
  tempFrom: string | null | undefined,
  tempTo: string | null | undefined
): boolean {
  if (!tempFrom || !tempTo) return true;
  const dateDay = date.startOf('day');
  return (
    !dateDay.isBefore(dayjs(tempFrom).startOf('day')) &&
    !dateDay.isAfter(dayjs(tempTo).endOf('day'))
  );
}

type DriverWindow = GetDriverWindowSlotsQuery['driver_window'][number];

function collectDistrictIdsForWeekday(
  driverWindows: DriverWindow[],
  weekday: number,
  selectedDate: Dayjs
): Set<string> {
  const ids = new Set<string>();

  const matchingWeekdayWindows = driverWindows.flatMap((slot) =>
    slot.weekday_windows.filter((ww) => ww.weekday === weekday)
  );

  for (const ww of matchingWeekdayWindows) {
    for (const wwd of ww.weekday_window_districts) {
      if (wwd.districtId && isTempDistrictActive(selectedDate, wwd.tempDateFrom, wwd.tempDateTo)) {
        ids.add(wwd.districtId);
      }
    }
  }

  return ids;
}

export function useTrackingPolygons(
  departmentId: number,
  selectedDriverId: string | undefined,
  selectedDate: Dayjs
): TrackingPolygon[] {
  const { data: districtsData } = useGetDriverDistricts({ departmentId });
  const { data: driverSlotsData } = useGetDriverWindowSlots({
    driverId: selectedDriverId ?? '',
  });

  const allPolygons = useMemo<TrackingPolygon[]>(() => {
    if (!districtsData?.district) return [];
    return districtsData.district
      .filter((d) => d.districts_coordinates.length > 0)
      .map((d, index) => ({
        id: d.id,
        name: d.name,
        coordinates: d.districts_coordinates.map((c) => ({
          lat: Number(c.latitude),
          lng: Number(c.longitude),
        })),
        color: POLYGON_COLORS[index % POLYGON_COLORS.length],
      }));
  }, [districtsData]);

  const driverDistrictIds = useMemo<Set<string>>(() => {
    if (!selectedDriverId || !driverSlotsData?.driver_window) return new Set();
    const weekday = getDbWeekday(selectedDate);
    return collectDistrictIdsForWeekday(driverSlotsData.driver_window, weekday, selectedDate);
  }, [selectedDriverId, driverSlotsData, selectedDate]);

  return useMemo<TrackingPolygon[]>(() => {
    if (!selectedDriverId) return allPolygons;
    return allPolygons.filter((p) => driverDistrictIds.has(p.id));
  }, [selectedDriverId, allPolygons, driverDistrictIds]);
}
